import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, Spinner, Button,
} from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import {
  createInvoice,
  updateInvoice,
  saveInvoiceGroupLines,
  uploadFileToInvoice,
  removeInvoiceAttachment,
  getInvoiceSignedUrls,
  uploadStagingFile,
  getStagingSignedUrls,
  removeStagingObject,
  extractInvoiceFromFile,
  formatCurrency,
  formatInvoiceNo,
  getNextInvoiceNoPeek,
  getInvoiceGroup,
  computeLineMoney,
  computeRowSubtotal,
  resolveRateType,
  flatAmountOf,
  RATE_TYPES,
} from "./Api";
import { PayablesCalendarPanel } from "./PayablesCalendarPanel";
import { readInvoiceDraft, writeInvoiceDraft, clearInvoiceDraft } from "./invoiceDraft";
import {
  uid, DEFAULT_GST_RATE, MAX_INVOICE_FILES, groupsFromItems, linesPayloadFromGroups,
  sharedAttachmentKeys, buildingChoicesByProperty, addBuildingSections,
  firstInvalidSection, submitErrorMessage,
  restrictPropertiesToGroup, sectionInGroup, groupBuildingPicks, companyOfSections,
  groupBuildingsByProperty, sectionsForGroup,
  sectionBuildings, rowBuildingName, withSectionBuildings, buildingsOutsideGroup,
} from "./invoiceFormGroups";
import { listBillingGroups, getEffectiveTemplate, previewEffectivePdf } from "./templateApi";
import { BuildingPickerModal } from "./BuildingPickerModal";
import { printInvoice, downloadInvoicePdf, prefetchPdfLibs } from "./InvoicePrintView";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? BLUE : "#E2E8F0",
    borderRadius: 10,
    minHeight: 46,
    fontSize: "0.85rem",
    boxShadow: isFocused ? "0 0 0 2px rgba(37,99,235,0.15)" : "none",
    backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
    "&:hover": { borderColor: BLUE },
  }),
  valueContainer: (base) => ({ ...base, paddingLeft: 36 }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.85rem",
    background: isSelected ? BLUE : isFocused ? "#EFF6FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 10, border: "1px solid #E2E8F0", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const LABEL_STYLE = {
  fontSize: "0.8rem", fontWeight: 600, color: "#334155", marginBottom: 6,
};

const FIELD_LABEL = { fontSize: "0.75rem", color: "#334155", fontWeight: 600, marginBottom: 5 };

const INPUT_STYLE = {
  borderColor: "#E2E8F0",
  borderRadius: 10,
  fontSize: "0.85rem",
  height: 46,
};

const INPUT_ICON_STYLE = { ...INPUT_STYLE, paddingLeft: 38 };

const CARD_STYLE = {
  background: "#fff", border: "1px solid #E7EAF0", borderRadius: 14, padding: "18px 20px",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const SECTION_TITLE = { fontSize: "0.95rem", fontWeight: 800, color: "#0F172A" };

// Card heading: an icon tile, the title, and a hairline rule to the card edge.
// The rule is what separates one section from the next now that the numbered
// badges have moved to the Property & Building repeater, where the numbering
// actually means something.
const SectionHeading = ({ icon, title, subtitle, right }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{
        width: 30, height: 30, borderRadius: 9, flexShrink: 0,
        background: "#EFF6FF", border: "1px solid #DBEAFE",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <FeatherIcon icon={icon} size={15} color={BLUE} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={SECTION_TITLE}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: "0.74rem", color: "#64748B", marginTop: 2, fontWeight: 400 }}>
            {subtitle}
          </div>
        )}
      </div>
      {right}
    </div>
    <div style={{ height: 1, background: "#E7EAF0", marginTop: 12 }} />
  </div>
);

// A field the read-only View renders as text. Matches INPUT_STYLE's box so the
// View keeps the form's rhythm without pretending to be editable.
const STATIC_FIELD = {
  ...INPUT_STYLE,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0 12px",
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  minWidth: 0,
};

const STATIC_FIELD_TEXT = {
  color: "#0F172A",
  fontWeight: 600,
  fontSize: "0.85rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ROW_BTN = {
  width: 40, height: 46, borderRadius: 10,
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", flexShrink: 0,
};


// Overlays a small leading icon on the wrapped input/select (which must reserve
// left padding for it).
const WithIcon = ({ icon, children }) => (
  <div style={{ position: "relative" }}>
    {children}
    <FeatherIcon
      icon={icon} size={15} color="#94A3B8"
      style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
    />
  </div>
);

// Free-text line description with the category's preset descriptions offered
// underneath. Typing filters the presets; anything typed is kept as-is, so a
// custom description needs no extra step. The chevron lists every preset.
const DescriptionCombo = ({ value, onChange, options, disabled }) => {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState(-1);
  const text = String(value || "").trim().toLowerCase();
  const shown = showAll || !text
    ? options
    : options.filter((o) => o.toLowerCase().includes(text) && o.toLowerCase() !== text);
  const menuOpen = open && !disabled && shown.length > 0;

  const close = () => { setOpen(false); setShowAll(false); setActive(-1); };
  const pick = (opt) => { onChange(opt); close(); };

  const onKeyDown = (e) => {
    if (!menuOpen) {
      if (e.key === "ArrowDown" && options.length) { setOpen(true); setActive(0); e.preventDefault(); }
      return;
    }
    if (e.key === "ArrowDown") { setActive((i) => Math.min(i + 1, shown.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setActive((i) => Math.max(i - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter" && active >= 0) { pick(shown[active]); e.preventDefault(); }
    else if (e.key === "Escape") { close(); e.preventDefault(); e.stopPropagation(); }
  };

  return (
    <div style={{ position: "relative" }}>
      <WithIcon icon="file-text">
        <Input
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); setShowAll(false); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onBlur={close}
          onKeyDown={onKeyDown}
          placeholder={options.length ? "Pick or type a description" : "e.g. Lawn care"}
          aria-label="Line description"
          aria-autocomplete="list"
          aria-expanded={menuOpen}
          autoComplete="off"
          style={{ ...INPUT_ICON_STYLE, paddingRight: options.length ? 34 : undefined }}
          disabled={disabled}
        />
      </WithIcon>
      {options.length > 0 && !disabled && (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Show preset descriptions"
          // mousedown, not click: keeps focus in the input so blur doesn't close the menu first.
          onMouseDown={(e) => {
            e.preventDefault();
            if (menuOpen && showAll) close();
            else { setOpen(true); setShowAll(true); setActive(-1); }
          }}
          style={{
            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
            border: "none", background: "transparent", padding: 4, cursor: "pointer", lineHeight: 0,
          }}
        >
          <FeatherIcon icon="chevron-down" size={15} color="#64748B" />
        </button>
      )}
      {menuOpen && (
        <div
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
            background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)", maxHeight: 220, overflowY: "auto", padding: 4,
          }}
        >
          {shown.map((opt, i) => (
            <div
              key={opt}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: "8px 10px", borderRadius: 7, fontSize: "0.82rem", cursor: "pointer",
                background: i === active ? "#EFF6FF" : "transparent",
                color: "#0F172A",
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// One row's Subtotal · GST · Grand Total — a compact copy of the invoice totals
// bar at the foot of the Properties card, pushed to the right of the note.
const RowTotals = ({ subtotal, gst, total }) => {
  const cell = {
    padding: "5px 18px", minWidth: 130, textAlign: "right",
    display: "flex", flexDirection: "column", justifyContent: "center",
  };
  const label = { fontSize: "0.64rem", color: "#fff", fontWeight: 700, lineHeight: 1.2 };
  const amount = { fontSize: "0.84rem", color: "#fff", fontWeight: 800, lineHeight: 1.3 };
  return (
    <div style={{
      marginLeft: "auto", display: "flex", alignItems: "stretch", minHeight: 44,
      background: "#1E3A8A", borderRadius: 10, overflow: "hidden", borderLeft: "4px solid #F59E0B",
    }}>
      <div style={cell}>
        <div style={label}>Subtotal</div>
        <div style={amount}>{formatCurrency(subtotal || 0)}</div>
      </div>
      <div style={cell}>
        <div style={label}>GST</div>
        <div style={amount}>{formatCurrency(gst || 0)}</div>
      </div>
      <div style={{ ...cell, padding: "5px 20px", minWidth: 160, background: "rgba(255,255,255,0.14)" }}>
        <div style={{ ...label, color: "#FCD34D" }}>Grand Total</div>
        <div style={{ ...amount, fontSize: "0.95rem", color: "#FBBF24", fontWeight: 900 }}>{formatCurrency(total || 0)}</div>
      </div>
    </div>
  );
};

// Hourly | Flat for one property section. A real radio group: arrow keys move
// between the options, the chosen one is announced, and the 46px body sits
// level with the Property and Building selectors beside it.
const RateTypeToggle = ({ name, value, onChange, disabled }) => (
  <div
    className={`iv-rate-toggle${disabled ? " is-locked" : ""}`}
    role="radiogroup"
    aria-label="Rate type"
  >
    {RATE_TYPES.map((opt) => (
      <label key={opt.value} className="iv-rate-opt">
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          disabled={disabled}
        />
        <span className="iv-rate-opt__body">
          <span className="iv-rate-opt__dot" aria-hidden="true" />
          {opt.label}
        </span>
      </label>
    ))}
  </div>
);

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const FilePreviewModal = ({ isOpen, toggle, signedUrl, fileKey }) => {
  const type = fileKey ? getFileTypeFromKey(fileKey) : "unknown";
  const fileName = fileKey ? fileKey.split("/").pop() : "Attachment";
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>{fileName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {signedUrl ? (
          <Previewer signedUrl={signedUrl} type={type} height="72vh" width="100%" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "72vh" }}>
            <Spinner style={{ color: "#A5B4FC" }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={fileName}
            style={{
              textDecoration: "none", background: BLUE, color: "#fff", borderRadius: 8,
              padding: "8px 14px", fontSize: "0.8rem", fontWeight: 700,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FeatherIcon icon="download" size={14} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

// Confidence < this is flagged so the reviewer double-checks that field.
const LOW_CONF = 0.5;

const ConfidencePill = ({ score }) => {
  if (typeof score !== "number") return null;
  const low = score < LOW_CONF;
  return (
    <span style={{
      fontSize: "0.66rem", fontWeight: 700, borderRadius: 20, padding: "2px 8px",
      background: low ? "#FEF3C7" : "#DCFCE7",
      color: low ? "#B45309" : "#15803D",
    }}>
      {low ? "Please confirm" : "High confidence"}
    </span>
  );
};

const DetailRow = ({ label, value, muted, badge }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid #F1F5F9" }}>
    <span style={{ fontSize: "0.72rem", color: "#64748B", fontWeight: 600, width: 120, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: "0.82rem", color: muted ? "#94A3B8" : "#0F172A", fontWeight: 600, flex: 1, minWidth: 0 }}>
      {value}
    </span>
    {badge}
  </div>
);

// Shows what the AI read from the invoice and lets the user accept it into the
// form or dismiss it and fill manually.
const PrefillReviewModal = ({ isOpen, suggestion, onConfirm, onDismiss }) => {
  if (!isOpen || !suggestion) return null;
  const conf = suggestion.confidence || {};
  const groups = suggestion.groups || [];

  const grandTotal = groups.reduce((sum, g) =>
    sum + (g.rows || []).reduce((a, r) =>
      a + (computeLineMoney(rowSubtotal(r, resolveRateType(r)), r.gstRate).total || 0), 0
    ), 0);

  // The extractor reports the vendor's number per detected section, but one paper
  // invoice carries one number — preview the first it found, which is the one
  // applySuggestion writes to the form.
  const detectedRef = groups
    .map((g) => String(g?.referenceNo || "").trim())
    .find(Boolean) || "";

  const vendorValue = suggestion.vendorLabel
    ? suggestion.vendorLabel
    : suggestion.vendorNameOnInvoice
      ? `${suggestion.vendorNameOnInvoice} — not in your vendor list`
      : "Not detected";

  return (
    <Modal isOpen={isOpen} centered size="lg" backdrop="static" style={{ zIndex: 13000 }}
      contentClassName="iv-form-modal-content">
      <ModalHeader tag="div" style={{ borderBottom: "1px solid #EEF2F7", padding: "16px 22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: "linear-gradient(135deg,#6366F1,#2563EB)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
          }}>
            <FeatherIcon icon="zap" size={19} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "1.02rem", fontWeight: 800, color: "#0F172A" }}>We read your invoice</div>
            <div style={{ fontSize: "0.76rem", color: "#64748B", fontWeight: 500, marginTop: 2 }}>
              Review the details below, then choose to prefill or fill manually.
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody style={{ padding: "18px 22px", maxHeight: "60vh", overflowY: "auto" }}>
        <div style={{ ...CARD_STYLE, padding: "12px 16px", marginBottom: 12 }}>
          <DetailRow label="Vendor" value={vendorValue} muted={!suggestion.vendorLabel} badge={<ConfidencePill score={conf.vendor} />} />
          <DetailRow label="Category" value={suggestion.categoryLabel || "Not detected"} muted={!suggestion.categoryLabel} badge={<ConfidencePill score={conf.category} />} />
          <DetailRow label="Title" value={suggestion.title || "—"} muted={!suggestion.title} />
          <DetailRow label="Vendor's Invoice #" value={detectedRef || "Not detected"} muted={!detectedRef} />
          {suggestion.description ? (
            <DetailRow label="Notes" value={suggestion.description} />
          ) : null}
        </div>

        {groups.map((g, gi) => (
          <div key={gi} style={{ ...CARD_STYLE, padding: "12px 16px", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FeatherIcon icon="home" size={14} color={BLUE} />
              <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#0F172A" }}>
                {g.propertyLabel || "Property not matched"}
              </span>
              {g.buildingName ? <span style={{ fontSize: "0.78rem", color: "#475569" }}>· {g.buildingName}</span> : null}
              <ConfidencePill score={conf.property} />
            </div>
            {(g.rows || []).map((r, ri) => {
              const rateType = resolveRateType(r);
              const total = computeLineMoney(rowSubtotal(r, rateType), r.gstRate).total;
              const parts = [];
              if (r.unitNo) parts.push(`Unit ${r.unitNo}`);
              if (r.jobDescription) parts.push(r.jobDescription);
              if (rateType === "flat") {
                parts.push(`Flat ${formatCurrency(Number(r.flatAmount) || 0)}`);
              } else {
                if (r.hours !== "" && r.hours != null) parts.push(`${r.hours}h${r.minutes ? ` ${r.minutes}m` : ""}`);
                if (r.amountPerHour !== "" && r.amountPerHour != null) parts.push(`@ ${formatCurrency(Number(r.amountPerHour) || 0)}/hr`);
              }
              if (r.materialCost) parts.push(`+ ${formatCurrency(Number(r.materialCost) || 0)} materials`);
              parts.push(`GST ${r.gstRate || 0}%`);
              return (
                <div key={ri} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: "0.78rem" }}>
                  <span style={{ color: "#475569" }}>{parts.join(" · ")}</span>
                  <span style={{ fontWeight: 800, color: "#0F172A" }}>{formatCurrency(total)}</span>
                </div>
              );
            })}
          </div>
        ))}

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#1E3A8A", borderRadius: 10, padding: "12px 18px", marginTop: 4,
        }}>
          <span style={{ color: "#FCD34D", fontWeight: 700, fontSize: "0.78rem" }}>Detected Grand Total</span>
          <span style={{ color: "#FBBF24", fontWeight: 900, fontSize: "1.1rem" }}>{formatCurrency(grandTotal)}</span>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-start" }}>
          <FeatherIcon icon="info" size={14} color="#94A3B8" style={{ marginTop: 2, flexShrink: 0 }} />
          <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
            AI suggestions can be wrong — anything you prefill stays fully editable, and flagged fields are worth a second look.
          </span>
        </div>
      </ModalBody>
      <ModalFooter style={{ borderTop: "1px solid #E9EEF5", padding: "14px 22px", background: "#F8FAFC", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" onClick={onDismiss} style={{
          height: 42, fontWeight: 600, fontSize: "0.84rem", borderRadius: 10, padding: "0 18px",
          background: "#fff", border: "1px solid #E2E8F0", color: "#334155", cursor: "pointer",
        }}>
          No, I'll fill manually
        </button>
        <button type="button" onClick={onConfirm} style={{
          height: 42, fontWeight: 700, fontSize: "0.84rem", borderRadius: 10, padding: "0 20px",
          background: BLUE, border: "none", color: "#fff", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
        }}>
          <FeatherIcon icon="check" size={15} /> Yes, prefill now
        </button>
      </ModalFooter>
    </Modal>
  );
};


// Each row (= one line of the invoice) chooses its own rate type, hourly or
// flat. A row keeps BOTH pricing field sets while it is being edited, so
// flipping the type and back loses nothing typed. Only the live set is
// validated and submitted — see handleSave. Hourly is the default because it
// is what every line was before the choice existed.
const blankRow = () => ({
  id: uid(),
  rateType: "hourly",
  jobDescription: "",
  unitNo: "",
  hours: "",
  minutes: "",
  amountPerHour: "",
  flatAmount: "",
  materialCost: "",
  gstRate: DEFAULT_GST_RATE,
  comment: "",
  files: [],
});

// One property/building section holding one or more unit rows. On create, every
// row becomes its own line of the same invoice. The vendor's own invoice number
// is not here — it is one invoice-level value on the form root.
const blankGroup = (propertyId = "", buildingName = "") => ({
  id: uid(),
  propertyId,
  buildingName,
  rows: [blankRow()],
});

/**
 * A row's pre-GST subtotal.
 *
 * While editing, it is derived from what is typed. In the read-only View the
 * row carries `storedAmount`, the figure actually recorded on the invoice, and
 * that wins: a line priced as a flat amount has no hours or rate to derive
 * from, so recomputing it would render a real charge as $0.00 and understate
 * the invoice total.
 */
// Last entry of every section's Building dropdown: opens the checklist that adds
// one section per ticked building.
const MULTI_BUILDING_OPTION = { value: "__multiple__", label: "Multiple buildings…" };

const rowSubtotal = (row, rateType) =>
  typeof row?.storedAmount === "number"
    ? row.storedAmount
    : computeRowSubtotal(row, rateType);

const InvoiceFormModal = ({
  isOpen,
  onClose,
  invoiceData,
  isNew,
  properties = [],
  vendors,
  vendorLocked = false,
  vendorName = "",
  vendorCategoryName = "",
  vendorCategories = [],
  prefilledPropertyId,
  prefilledBuildingName,
  prefilledPropertyLabel,
  restoreDraft = false,
  readOnly = false,
}) => {
  const locked = !!readOnly;
  const initial = useMemo(() => ({
    title: "",
    vendorId: "",
    categoryId: "",
    description: "",
    // The vendor's own invoice number, typed once for the whole submission. The
    // server copies it onto every line, so it is never entered per section.
    referenceNo: "",
    // The vendor's property group this invoice bills ("" = none). See BillingGroupsTab.
    billingGroupId: "",
    groups: [prefilledPropertyId ? blankGroup(prefilledPropertyId, prefilledBuildingName || "") : blankGroup()],
  }), [prefilledPropertyId, prefilledBuildingName]);

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const submitLock = useRef(false);
  const [createdId, setCreatedId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewStaged, setPreviewStaged] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [aiScanning, setAiScanning] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  // Index of the section whose "Multiple buildings…" checklist is open, else null.
  const [buildingPickerFor, setBuildingPickerFor] = useState(null);
  const fileInputRef = useRef(null);
  // Cards a failed submit scrolls to (see firstInvalidSection).
  const sectionRefs = { details: useRef(null), lines: useRef(null), file: useRef(null) };
  const rowFileInputRef = useRef(null);
  const rowFileTargetRef = useRef(null);
  // Keys already sent to the AI, so a given upload is scanned at most once.
  const scannedKeysRef = useRef(new Set());
  // The RD number this submission will draw, peeked for a read-only preview.
  const [nextNo, setNextNo] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Vendor property groups (vendor only), and the approved template that will
  // generate this invoice, if any: { template: { _id, name, version } | null, nextNumber }.
  const [billingGroups, setBillingGroups] = useState([]);
  const [effective, setEffective] = useState(null);
  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const [templatePreviewUrl, setTemplatePreviewUrl] = useState(null);
  const [templatePreviewing, setTemplatePreviewing] = useState(false);
  // The Vendor's Invoice # this form filled in itself from the template's
  // numbering; replaced when the suggestion changes, never over a typed value.
  const autoRefNo = useRef("");

  const cleanupStaged = (files) => {
    (files || []).forEach((f) => {
      if (f.key) removeStagingObject(f.key).catch(() => {});
    });
  };

  // The RD sequence belongs to the company that owns the property being invoiced.
  // Internal roles carry that company on their token, but a vendor does not, so
  // the peek has to name a property. Every section of one submission must belong
  // to the same company, so the first chosen property settles it.
  const peekPropertyId = (form.groups || []).map((g) => g.propertyId).find(Boolean) || "";

  useEffect(() => {
    if (!isOpen || !isNew || locked) return;
    let cancelled = false;
    setNextNo(null);
    getNextInvoiceNoPeek(peekPropertyId).then((n) => { if (!cancelled) setNextNo(n); });
    return () => { cancelled = true; };
  }, [isOpen, isNew, locked, peekPropertyId]);

  // The number this invoice will receive. A concurrent creation can shift it, so
  // it is shown as a preview and never written into an editable field.
  const rdPreview = nextNo == null ? "Assigned on submit" : `RD ${nextNo}`;

  // Read-only preview: warm the pdf libs so Download PDF is instant.
  useEffect(() => { if (isOpen && locked) prefetchPdfLibs(); }, [isOpen, locked]);

  // The View shows the WHOLE invoice, laid out the way it was entered — and so
  // does Edit now: every sibling line in the group loads as its own editable
  // row, saved back in one request (see handleSave). Only a brand-new,
  // unsaved invoice (isNew) has no group to fetch yet.
  const [previewGroup, setPreviewGroup] = useState(null);
  // True from the moment the modal opens on a grouped invoice until its lines
  // have arrived. While it is true the rows are not rendered and Save is
  // disabled: the single-line fallback the form starts from is NOT the real
  // invoice, and letting the user type into it only for the fetch to replace
  // every row a second later silently threw their edits away.
  const [groupLoading, setGroupLoading] = useState(false);
  // Set when the group fetch came back without any lines: the form is left
  // showing the single-line placeholder with no way to know what else the
  // invoice held, so it must not be saved.
  const [groupLoadFailed, setGroupLoadFailed] = useState(false);

  // Sections and rows can be added or removed while creating, and while editing
  // any invoice that has a group to save them into. An invoice raised before
  // batching has no groupId and is still saved one document at a time, so its
  // line set stays fixed. A grouped invoice may only be edited once every line
  // has loaded: saving the single-line placeholder through the whole-group
  // endpoint would delete the lines that never arrived.
  //
  // The GET reports a partial view when the caller is scoped to some of the
  // invoice's properties. Those lines cannot be edited here — saving would send
  // back only the visible ones — so Edit refuses up front rather than letting
  // the user retype the form and meet a 404 on Save.
  const groupPartial = !isNew && !!previewGroup?.isPartial;
  const groupPending = !isNew && !!invoiceData?.groupId && (groupLoading || groupLoadFailed || groupPartial);
  const canEditLines = !locked && (isNew || (!!invoiceData?.groupId && !groupPending));
  // Rows and totals are hidden while what the form holds is not the whole
  // invoice. The read-only View keeps showing its visible portion, under the
  // "Showing X of Y items" banner below.
  const linesUnusable = groupLoading || groupLoadFailed || (!locked && groupPartial);

  useEffect(() => {
    if (!isOpen || isNew || !invoiceData?.groupId) {
      setPreviewGroup(null);
      setGroupLoading(false);
      setGroupLoadFailed(false);
      return undefined;
    }
    let cancelled = false;
    setGroupLoading(true);
    setGroupLoadFailed(false);
    getInvoiceGroup(invoiceData.groupId).then((g) => {
      if (cancelled) return;
      setPreviewGroup(g);
      setGroupLoading(false);
      // The helper swallows failures and returns null. In Edit that would leave
      // the single-line fallback on screen looking like the whole invoice, so
      // mark the load failed: that blocks Save, which would otherwise send the
      // placeholder to the whole-group endpoint and delete the missing lines.
      setGroupLoadFailed(!g?.items?.length);
      if (!g && !locked) {
        toast.warning("Could not load every line of this invoice. Close and reopen it to edit; nothing was saved.");
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, isNew, locked, invoiceData?.groupId]);

  useEffect(() => {
    const items = previewGroup?.items;
    if (!isOpen || isNew || !items?.length) return;
    const groups = groupsFromItems(items, { editable: !locked });
    setForm((p) => ({
      ...p,
      referenceNo: previewGroup.referenceNo || p.referenceNo,
      // Edit rows must not pin themselves to the recorded amount, or the totals
      // would never move as the user types — see groupsFromItems.
      groups,
    }));
    // The Invoice File card holds only the files every line shares; a line's
    // own files are shown on its row instead.
    setAttachments(sharedAttachmentKeys(items));
    const rowKeys = groups.flatMap((g) => g.rows.flatMap((r) => r.files.map((f) => f.key)));
    if (rowKeys.length) {
      getInvoiceSignedUrls(rowKeys).then((urls) => {
        if (!urls) return;
        setForm((p) => ({
          ...p,
          groups: p.groups.map((g) => ({
            ...g,
            rows: g.rows.map((r) => ({
              ...r,
              files: (r.files || []).map((f) => (f.saved && urls[f.key] ? { ...f, signedUrl: urls[f.key] } : f)),
            })),
          })),
        }));
      }).catch(() => {});
    }
  }, [isOpen, isNew, locked, previewGroup]);

  useEffect(() => {
    if (!isOpen) return;
    if (isNew) {
      const draft = restoreDraft ? readInvoiceDraft() : null;
      if (draft?.form) {
        // Rebuild the autosaved draft: form values plus references to the staged
        // uploads (which survive a crash), with fresh signed URLs for previews.
        const rowKeys = [];
        const groups = (draft.form.groups || []).map((g) => ({
          ...blankGroup(),
          ...g,
          rows: (g.rows || []).map((r) => ({
            ...blankRow(),
            ...r,
            // Drafts saved while the type was chosen per section carry it on
            // the section; a row without its own inherits that, else hourly.
            rateType: r.rateType || g.rateType || "hourly",
            files: (r.files || [])
              .filter((f) => f?.key)
              .map((f) => {
                rowKeys.push(f.key);
                return { ...f, uploading: false };
              }),
          })),
        }));
        const restoredPending = (draft.pendingFiles || [])
          .filter((f) => f?.key)
          .map((f) => ({
            id: f.id || uid(), name: f.name || "attachment", key: f.key,
            file: null, signedUrl: null, uploading: false, error: false,
          }));
        setForm({ ...initial, ...draft.form, groups: groups.length ? groups : [blankGroup()] });
        setPendingFiles(restoredPending);
        const keys = [...restoredPending.map((f) => f.key), ...rowKeys];
        if (keys.length) {
          getStagingSignedUrls(keys).then((urls) => {
            if (!urls) return;
            setPendingFiles((prev) => prev.map((f) => (urls[f.key] ? { ...f, signedUrl: urls[f.key] } : f)));
            setForm((p) => ({
              ...p,
              groups: p.groups.map((g) => ({
                ...g,
                rows: g.rows.map((r) => ({
                  ...r,
                  files: (r.files || []).map((f) => (urls[f.key] ? { ...f, signedUrl: urls[f.key] } : f)),
                })),
              })),
            }));
          }).catch(() => {});
        }
      } else {
        setForm(initial);
        cleanupStaged(pendingFiles);
        setPendingFiles([]);
      }
      setCreatedId(null);
      setAttachments([]);
      setSignedUrls({});
      setAiSuggestion(null);
      setAiScanning(false);
      scannedKeysRef.current = new Set();
    } else if (invoiceData) {
      const refs = Array.isArray(invoiceData.properties) && invoiceData.properties.length
        ? invoiceData.properties
        : [];
      const firstRef = refs[0] || {
        propertyId: prefilledPropertyId || "",
        buildingName: prefilledBuildingName || "",
      };
      setForm({
        title: invoiceData.title || "",
        vendorId: invoiceData.vendorId || invoiceData.vendor?._id || "",
        categoryId: invoiceData.categoryId || invoiceData.category?._id || "",
        description: invoiceData.description || "",
        referenceNo: invoiceData.referenceNo || "",
        billingGroupId: invoiceData.billingGroup?.groupId ? String(invoiceData.billingGroup.groupId) : "",
        groups: [{
          id: uid(),
          propertyId: firstRef.propertyId || "",
          buildingName: firstRef.buildingName || "",
          propertyLabel: firstRef.title || invoiceData.propertyTitle || "",
          rows: [{
            id: uid(),
            itemId: invoiceData._id,
            rateType: resolveRateType(invoiceData),
            jobDescription: firstRef.jobDescription || "",
            unitNo: firstRef.unitNo || "",
            hours: invoiceData.hours ?? "",
            minutes: invoiceData.minutes ?? "",
            amountPerHour: invoiceData.amountPerHour ?? "",
            flatAmount: resolveRateType(invoiceData) === "flat" ? flatAmountOf(invoiceData) : "",
            materialCost: invoiceData.materialCost ?? "",
            gstRate: invoiceData.gstRate ?? DEFAULT_GST_RATE,
            comment: invoiceData.comment || "",
            files: [],
          }],
        }],
      });
      setCreatedId(invoiceData._id);
      setAttachments(invoiceData.attachments || []);
    }
    setErrors({});
  }, [isOpen, isNew, invoiceData, initial, prefilledPropertyId, prefilledBuildingName, restoreDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!attachments.length) {
      setSignedUrls({});
      return;
    }
    const missing = attachments.filter((k) => !signedUrls[k]);
    if (!missing.length) return;
    (async () => {
      const urls = await getInvoiceSignedUrls(missing);
      setSignedUrls((prev) => ({ ...prev, ...urls }));
    })();
  }, [attachments]); // eslint-disable-line

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  // ── Property groups ──────────────────────────────────────────────────────
  // A vendor loads their own groups (archived kept only so an invoice raised for
  // one still shows its group). Staff load the selected vendor's active groups;
  // a group that doesn't belong to the newly picked vendor is dropped, except
  // the one the invoice was saved with.
  const savedBillingGroupId = invoiceData?.billingGroup?.groupId ? String(invoiceData.billingGroup.groupId) : "";
  useEffect(() => {
    if (!isOpen || locked) return undefined;
    if (!vendorLocked && !form.vendorId) { setBillingGroups([]); return undefined; }
    let cancelled = false;
    listBillingGroups(vendorLocked ? { includeArchived: "1" } : { vendorId: form.vendorId })
      .then((list) => {
        if (cancelled) return;
        const groups = Array.isArray(list) ? list : [];
        setBillingGroups(groups);
        if (vendorLocked) return;
        setForm((p) => (
          !p.billingGroupId || p.billingGroupId === savedBillingGroupId ||
          groups.some((g) => String(g._id) === String(p.billingGroupId))
            ? p
            : { ...p, billingGroupId: "" }
        ));
      })
      .catch(() => { if (!cancelled) setBillingGroups([]); });
    return () => { cancelled = true; };
  }, [isOpen, locked, vendorLocked, form.vendorId, savedBillingGroupId]);

  const selectedBillingGroup = useMemo(
    () => billingGroups.find((g) => String(g._id) === String(form.billingGroupId)) || null,
    [billingGroups, form.billingGroupId]
  );
  const billingGroupOptions = useMemo(() => [
    { value: "", label: vendorLocked ? "No group — any of my buildings" : "No group — any building" },
    ...billingGroups
      .filter((g) => g.isActive || String(g._id) === String(form.billingGroupId))
      .map((g) => ({ value: String(g._id), label: `${g.name} (${(g.buildings || []).length} building${(g.buildings || []).length !== 1 ? "s" : ""})` })),
  ], [billingGroups, form.billingGroupId, vendorLocked]);
  const showGroupPicker = (vendorLocked || !!form.vendorId) && !locked && canEditLines && billingGroupOptions.length > 1;

  // While a group is chosen, only its buildings are offered anywhere on the form.
  const scopedProperties = useMemo(
    () => restrictPropertiesToGroup(properties, locked ? null : selectedBillingGroup),
    [properties, selectedBillingGroup, locked]
  );

  const addGroupBuildings = () => {
    const picks = groupBuildingPicks(selectedBillingGroup);
    if (!picks.length) return;
    setForm((p) => {
      const empty = p.groups.findIndex((g) => !sectionBuildings(g).length);
      return { ...p, groups: addBuildingSections(p.groups, empty < 0 ? p.groups.length : empty, picks, blankGroup) };
    });
  };

  const propertyOptions = useMemo(
    () => (scopedProperties || [])
      .map((p) => ({ value: p._id, label: p.title || p.name || p._id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [scopedProperties]
  );

  // Accepts both the `building: [{buildingName}]` shape (internal property list)
  // and the `buildings: ["A", "B"]` shape (vendor eligibility list).
  const buildingOptionsFor = (propertyId) => {
    const prop = (scopedProperties || []).find((p) => p._id === propertyId);
    if (!prop) return [];
    const raw = Array.isArray(prop.buildings) && prop.buildings.length
      ? prop.buildings.map((b) => (typeof b === "string" ? b : b?.buildingName))
      : (prop.building || []).map((b) => b?.buildingName);
    return raw.filter(Boolean).map((b) => ({ value: b, label: b }));
  };

  const updateGroup = (gi, patch) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)),
    }));

  const updateRow = (gi, ri, patch) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) =>
        i === gi
          ? { ...g, rows: g.rows.map((r, j) => (j === ri ? { ...r, ...patch } : r)) }
          : g
      ),
    }));

  const addRow = (gi) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) => (i === gi ? { ...g, rows: [...g.rows, blankRow()] } : g)),
    }));

  const removeRow = (gi, ri) => {
    const group = form.groups[gi];
    if (!group || group.rows.length <= 1) return;
    cleanupStaged(group.rows[ri]?.files);
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) =>
        i === gi ? { ...g, rows: g.rows.filter((_, j) => j !== ri) } : g
      ),
    }));
  };

  const removeGroup = (gi) => {
    if (form.groups.length <= 1) return;
    (form.groups[gi]?.rows || []).forEach((r) => cleanupStaged(r.files));
    setForm((p) => ({ ...p, groups: p.groups.filter((_, i) => i !== gi) }));
  };

  const buildingChoices = useMemo(() => buildingChoicesByProperty(scopedProperties), [scopedProperties]);

  const addPickedBuildings = (picks) => {
    const gi = buildingPickerFor;
    setBuildingPickerFor(null);
    if (gi == null || !picks.length) return;
    setForm((p) => ({ ...p, groups: addBuildingSections(p.groups, gi, picks, blankGroup) }));
  };

  // When a property is chosen and it has exactly one building, auto-select it.
  const onPickProperty = (gi, propertyId) => {
    const buildings = buildingOptionsFor(propertyId);
    setGroupBuildings(gi, buildings.length === 1 ? [buildings[0].value] : [], { propertyId });
  };

  // Replace a section's buildings (see withSectionBuildings).
  const setGroupBuildings = (gi, names, patch = {}) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) => (i === gi ? withSectionBuildings({ ...g, ...patch }, names) : g)),
    }));

  // ── Per-row supporting attachments (staged, adopted by the split on create) ──
  const patchRowFiles = (groupId, rowId, updater) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g) =>
        g.id !== groupId
          ? g
          : { ...g, rows: g.rows.map((r) => (r.id !== rowId ? r : { ...r, files: updater(r.files || []) })) }
      ),
    }));

  const openRowFilePicker = (groupId, rowId) => {
    rowFileTargetRef.current = { groupId, rowId };
    rowFileInputRef.current?.click();
  };

  const handleRowFiles = async (fileList) => {
    const target = rowFileTargetRef.current;
     const files = Array.from(fileList || []);
    if (rowFileInputRef.current) rowFileInputRef.current.value = "";
    if (!target || !files.length) return;
    setErrors((p) => (p.attachments ? { ...p, attachments: undefined } : p));
    const { groupId, rowId } = target;
    for (const file of files) {
      const pf = { id: uid(), name: file.name, key: null, signedUrl: null, uploading: true };
      patchRowFiles(groupId, rowId, (prev) => [...prev, pf]);
      try {
        const key = await uploadStagingFile(file);
        const urls = await getStagingSignedUrls([key]);
        patchRowFiles(groupId, rowId, (prev) =>
          prev.map((f) => (f.id === pf.id ? { ...f, key, signedUrl: urls[key] || null, uploading: false } : f))
        );
      } catch (err) {
        console.error("row staging upload failed", err);
        patchRowFiles(groupId, rowId, (prev) => prev.filter((f) => f.id !== pf.id));
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const removeRowFile = (groupId, rowId, fileId) => {
    const group = (form.groups || []).find((g) => g.id === groupId);
    const row = (group?.rows || []).find((r) => r.id === rowId);
    const file = (row?.files || []).find((f) => f.id === fileId);
    if (file?.key) removeStagingObject(file.key).catch(() => {});
    patchRowFiles(groupId, rowId, (prev) => prev.filter((f) => f.id !== fileId));
  };

  const anyRowFileUploading = (form.groups || []).some((g) =>
    (g.rows || []).some((r) => (r.files || []).some((f) => f.uploading))
  );

  // Vendor options (active vendors). When editing an invoice whose vendor is no
  // longer active, keep a synthetic option so the selection still shows.
  const vendorOpts = useMemo(() => {
    const list = (vendors || [])
      .map((v) => ({
        value: v._id,
        label: v.name,
        categories:
          (v.categories && v.categories.length
            ? v.categories
            : v.category
              ? [v.category]
              : []) || [],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (!isNew && invoiceData?.vendorId && !list.some((o) => o.value === invoiceData.vendorId)) {
      list.unshift({
        value: invoiceData.vendorId,
        label: `${invoiceData.vendorName || "Vendor"} (inactive)`,
        categories: invoiceData.category ? [invoiceData.category] : [],
      });
    }
    return list;
  }, [vendors, isNew, invoiceData]);

  const selectedVendor = vendorOpts.find((o) => o.value === form.vendorId) || null;

  // Category choices: from the selected vendor (internal create), or the
  // eligibility-provided list (vendor self-submission).
  const categoryChoices = useMemo(() => {
    const fromVendor = vendorLocked ? vendorCategories : (selectedVendor?.categories || []);
    return (fromVendor || [])
      .map((c) => ({ value: c._id, label: c.name, descriptionOptions: c.descriptionOptions || [] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [vendorLocked, vendorCategories, selectedVendor]);

  // Preset line descriptions for the chosen category (offered, never enforced).
  const descriptionPresets = useMemo(
    () => categoryChoices.find((o) => o.value === form.categoryId)?.descriptionOptions || [],
    [categoryChoices, form.categoryId]
  );

  // Keep the chosen category consistent with the available choices: drop a stale
  // category when the vendor changes, and auto-select when only one is available.
  useEffect(() => {
    setForm((p) => {
      const valid = categoryChoices.some((o) => o.value === p.categoryId);
      let categoryId = valid ? p.categoryId : "";
      if (!categoryId && categoryChoices.length === 1) categoryId = categoryChoices[0].value;
      return categoryId === p.categoryId ? p : { ...p, categoryId };
    });
  }, [categoryChoices]); // eslint-disable-line

  // ── Invoice template ─────────────────────────────────────────────────────
  // Once the company has approved one of the vendor's templates, the server
  // generates the invoice PDF from it and attaching a file becomes optional.
  // The server makes the final call; this only mirrors it on screen.
  const formCompanyId = companyOfSections(properties, form.groups) ||
    (selectedBillingGroup?.companyId ? String(selectedBillingGroup.companyId) : "");
  const effectiveVendorId = vendorLocked ? "" : form.vendorId;
  useEffect(() => {
    if (!isOpen || !isNew || locked) { setEffective(null); return undefined; }
    if (vendorLocked ? !formCompanyId : !effectiveVendorId) { setEffective(null); return undefined; }
    let cancelled = false;
    setEffectiveLoading(true);
    const t = setTimeout(() => {
      const params = {};
      if (formCompanyId) params.companyId = formCompanyId;
      if (form.billingGroupId) params.billingGroupId = form.billingGroupId;
      if (effectiveVendorId) params.vendorId = effectiveVendorId;
      getEffectiveTemplate(params)
        .then((r) => { if (!cancelled) setEffective(r?.template ? r : null); })
        .catch(() => { if (!cancelled) setEffective(null); })
        .finally(() => { if (!cancelled) setEffectiveLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isOpen, isNew, locked, vendorLocked, formCompanyId, form.billingGroupId, effectiveVendorId]);

  const templateApplies = isNew && !locked && !!effective?.template;

  // Fill Vendor's Invoice # from the template's numbering ("GSK 271") unless
  // the vendor typed their own; follow the suggestion while it is untouched.
  useEffect(() => {
    if (!isOpen || !isNew || locked) return;
    const suggestion = templateApplies ? (effective?.nextNumber || "") : "";
    setForm((p) => {
      const current = String(p.referenceNo || "");
      if (current && current !== autoRefNo.current) return p;
      autoRefNo.current = suggestion;
      return current === suggestion ? p : { ...p, referenceNo: suggestion };
    });
  }, [isOpen, isNew, locked, templateApplies, effective?.nextNumber]);

  // Live GST + total breakdown across every unit row.
  const allRows = (form.groups || []).flatMap((g) => g.rows || []);
  const lineMoney = allRows.map((r) =>
    computeLineMoney(rowSubtotal(r), r.gstRate)
  );
  const subtotalSum = lineMoney.reduce((a, m) => a + (m.subtotal || 0), 0);
  const gstSum = lineMoney.reduce((a, m) => a + (m.gstAmount || 0), 0);
  const totalSum = lineMoney.reduce((a, m) => a + (m.total || 0), 0);
  const totalRowCount = allRows.length;

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title is required";
    if (!vendorLocked && !form.vendorId) e.vendorId = "Vendor is required";
    if (!form.categoryId) e.categoryId = "Category is required";
    const groups = form.groups || [];
    if (!groups.length) {
      e.lines = "Add at least one property";
    } else {
      const seen = new Set();
      for (const g of groups) {
        if (e.lines) break;
        if (!g.propertyId) { e.lines = "Select a property for every section"; break; }
        const sectionNames = sectionBuildings(g);
        if (!sectionNames.length) { e.lines = "Choose a building for every property"; break; }
        const outside = locked ? [] : buildingsOutsideGroup(g, selectedBillingGroup);
        if (outside.length) {
          e.lines = `${outside[0]} is not in the group “${selectedBillingGroup.name}” — pick one of its buildings or change the group`;
          break;
        }
        for (const r of g.rows || []) {
          const rateType = r.rateType || "hourly";
          if (sectionNames.length > 1 && !sectionNames.includes(r.buildingName)) {
            e.lines = "Pick the building for every row in a section with several buildings"; break;
          }
          // A row must say what it is for: a Unit No., a Description, or both.
          if (!String(r.unitNo || "").trim() && !String(r.jobDescription || "").trim()) {
            e.lines = "Each row needs a Unit No. or a Description"; break;
          }
          if (rateType === "flat") {
            // One charge, never multiplied — it only has to be a valid amount.
            if (r.flatAmount === "" || r.flatAmount == null ||
                Number.isNaN(Number(r.flatAmount)) || Number(r.flatAmount) < 0) {
              e.lines = "Enter a valid flat amount for every row"; break;
            }
          } else {
            const hasHours = r.hours !== "" && r.hours != null;
            const hasMinutes = r.minutes !== "" && r.minutes != null;
            if (!hasHours && !hasMinutes) {
              e.lines = "Enter time for every row — hours, minutes, or both"; break;
            }
            if (hasHours && (!Number.isInteger(Number(r.hours)) || Number(r.hours) < 0)) {
              e.lines = "Hours must be a whole number"; break;
            }
            if (hasMinutes &&
                (!Number.isInteger(Number(r.minutes)) || Number(r.minutes) < 0 || Number(r.minutes) > 59)) {
              e.lines = "Minutes must be a whole number between 0 and 59"; break;
            }
            if (r.amountPerHour === "" || Number.isNaN(Number(r.amountPerHour)) || Number(r.amountPerHour) < 0) {
              e.lines = "Enter a valid amount per hour for every row"; break;
            }
          }
          if (r.materialCost !== "" && r.materialCost != null &&
              (Number.isNaN(Number(r.materialCost)) || Number(r.materialCost) < 0)) {
            e.lines = "Material cost must be a valid positive amount"; break;
          }
          const rate = Number(r.gstRate);
          if (Number.isNaN(rate) || rate < 0 || rate > 100) {
            e.lines = "GST must be between 0 and 100%"; break;
          }
          // The same property/building/unit may repeat for different jobs — only
          // fully identical rows are flagged.
          const key = [
            g.propertyId,
            rowBuildingName(g, r).toLowerCase(),
            (r.unitNo || "").trim().toLowerCase(),
            (r.jobDescription || "").trim().toLowerCase(),
            rateType,
            rateType === "flat" ? Number(r.flatAmount) : Number(r.hours),
            rateType === "flat" ? "" : Number(r.minutes) || 0,
            rateType === "flat" ? "" : Number(r.amountPerHour),
            Number(r.materialCost) || 0,
            Number(r.gstRate),
            (r.comment || "").trim().toLowerCase(),
          ].join("::");
          if (seen.has(key)) { e.lines = "Two rows are exactly identical — add a note or adjust one to tell the jobs apart"; break; }
          seen.add(key);
        }
      }
    }
    // Every new invoice carries supporting paperwork: the invoice file, or a file
    // on at least one row — unless an approved template generates it. The
    // server enforces the same rule.
    if (isNew && !templateApplies) {
      const hasInvoiceFile = pendingFiles.some((p) => p.key || p.uploading);
      const hasRowFile = (form.groups || []).some((g) =>
        (g.rows || []).some((r) => (r.files || []).some((f) => f.key || f.uploading))
      );
      if (!hasInvoiceFile && !hasRowFile) e.attachments = "Attach the invoice file before submitting";
    }
    setErrors(e);
    return e;
  };

  // Bring a card to the top of the screen; on a phone the error is otherwise
  // several screens below the Submit button the user just pressed.
  const scrollToSection = (section) => {
    sectionRefs[section]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // The invoice file is required on a new invoice; until one is attached (on
  // the invoice or on a row) a notice at the top of the form says so.
  const hasAnyFile =
    pendingFiles.some((p) => p.key || p.uploading) ||
    (form.groups || []).some((g) => (g.rows || []).some((r) => (r.files || []).length));
  const showFileRequiredNotice = isNew && !locked && !createdId && !hasAnyFile && !templateApplies && !effectiveLoading;

  // The create request for the form as it stands. Also what the template
  // preview sends, so the preview is built from exactly what would be saved.
  const buildCreatePayload = () => {
    const payload = {
      title: (form.title || "").trim(),
      description: (form.description || "").trim(),
      // One invoice-level vendor reference; the server copies it onto every line.
      referenceNo: (form.referenceNo || "").trim(),
      lines: form.groups.flatMap((g) =>
        g.rows.map((r) => {
          const rateType = r.rateType || "hourly";
          return {
            propertyId: g.propertyId,
            buildingName: rowBuildingName(g, r),
            unitNo: (r.unitNo || "").trim(),
            jobDescription: (r.jobDescription || "").trim(),
            rateType,
            // Only the live field set is sent. A flat line carries no time
            // fields at all, so nothing can ever be multiplied into it.
            ...(rateType === "flat"
              ? { flatAmount: Number(r.flatAmount) }
              : {
                  hours: Number(r.hours),
                  minutes: Number(r.minutes) || 0,
                  amountPerHour: Number(r.amountPerHour),
                }),
            materialCost: Number(r.materialCost) || 0,
            gstRate: Number(r.gstRate),
            comment: (r.comment || "").trim(),
            attachments: (r.files || []).map((f) => f.key).filter(Boolean),
          };
        })
      ),
      attachments: pendingFiles.map((p) => p.key).filter(Boolean),
      categoryId: form.categoryId,
    };
    if (!vendorLocked) payload.vendorId = form.vendorId;
    if (form.billingGroupId) payload.billingGroupId = form.billingGroupId;
    return payload;
  };

  const handleSave = async () => {
    if (locked) return;
    if (submitLock.current) return;
    const invalid = validate();
    if (Object.keys(invalid).length) {
      toast.error(submitErrorMessage(invalid));
      scrollToSection(firstInvalidSection(invalid));
      return;
    }
    if (pendingFiles.some((p) => p.uploading) || anyRowFileUploading) {
      toast.warning("Please wait for the files to finish uploading.");
      return;
    }
    submitLock.current = true;
    setSaving(true);
    try {
      if (isNew && !createdId) {
        const payload = buildCreatePayload();
        const res = await createInvoice(payload);
        const created = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        if (!created.length) {
          toast.error("Invoice was not created.");
          return;
        }
        setPendingFiles([]);
        clearInvoiceDraft();
        toast.success(res?.message || "Invoice submitted for approval.");
        onClose(true);
      } else if (invoiceData?.groupId) {
        // Belt and braces: the Save button is already disabled while the group
        // is loading or failed to load, because sending the placeholder here
        // would delete every line that never arrived.
        if (groupPending) {
          toast.error("This invoice has not fully loaded. Close and reopen it to edit.");
          return;
        }
        // A batched invoice is saved whole: every line the form holds, in one
        // request. Lines loaded from the server carry `_id` and are updated,
        // added rows are created, and any line no longer in the form is
        // deleted. The server sends a single "invoice updated" email.
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          referenceNo: (form.referenceNo || "").trim(),
          categoryId: form.categoryId,
          lines: linesPayloadFromGroups(form.groups),
          // Every line the form loaded; the server refuses the save if the invoice has grown since.
          knownLineIds: (previewGroup?.items || []).map((item) => String(item._id)),
        };
        if (!vendorLocked) payload.vendorId = form.vendorId;
        // Staff only see the vendor's active groups, so they send the group only
        // when they changed it; resending an archived one would be refused.
        if (vendorLocked || (form.billingGroupId || "") !== savedBillingGroupId) {
          payload.billingGroupId = form.billingGroupId || null;
        }
        await saveInvoiceGroupLines(invoiceData.groupId, payload);
        toast.success("Invoice updated.");
        onClose(true);
      } else {
        // An invoice raised before batching is one document with no group: a
        // single PATCH, exactly as before. Its line set cannot change.
        const group = form.groups[0] || {};
        const row = (group.rows || [])[0] || {};
        const rateType = row.rateType || "hourly";
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          referenceNo: (form.referenceNo || "").trim(),
          rateType,
          // The inactive set is sent blank so the server clears it: an hourly
          // line edited into a flat one must not keep its old rate on record.
          hours: rateType === "flat" ? "" : Number(row.hours),
          minutes: rateType === "flat" ? "" : Number(row.minutes) || 0,
          amountPerHour: rateType === "flat" ? "" : Number(row.amountPerHour),
          flatAmount: rateType === "flat" ? Number(row.flatAmount) : "",
          materialCost: Number(row.materialCost) || 0,
          gstRate: Number(row.gstRate),
          comment: (row.comment || "").trim(),
          unitNo: (row.unitNo || "").trim(),
          // Line-level only: a top-level `description` is the invoice's Notes.
          properties: [{
            propertyId: group.propertyId,
            buildingName: rowBuildingName(group, row),
            unitNo: (row.unitNo || "").trim(),
            jobDescription: (row.jobDescription || "").trim(),
          }],
          categoryId: form.categoryId,
        };
        if (!vendorLocked) payload.vendorId = form.vendorId;
        const targetId = row.itemId || invoiceData?._id || createdId;
        await updateInvoice(targetId, payload);
        toast.success("Invoice updated.");
        onClose(true);
      }
    } catch {
      // toast handled in api helper
    } finally {
      setSaving(false);
      submitLock.current = false;
    }
  };

  const invoiceId = createdId || invoiceData?._id;

  // True when the user hasn't started filling the form yet — the only time we
  // auto-open the AI suggestion so we never interrupt someone mid-entry.
  const isFormPristine = () =>
    !form.title?.trim() &&
    !form.vendorId &&
    (!String(form.referenceNo || "").trim() || form.referenceNo === autoRefNo.current) &&
    !form.billingGroupId &&
    (form.groups || []).every(
      (g) =>
        !g.propertyId &&
        (g.rows || []).every(
          (r) =>
            !String(r.unitNo || "").trim() &&
            !String(r.jobDescription || "").trim() &&
            r.hours === "" &&
            r.amountPerHour === "" &&
            (r.flatAmount === "" || r.flatAmount == null)
        )
    );

  // Autosave new-invoice work to a localStorage draft so a session timeout or
  // crash never loses it. Files are kept as staged-key references only.
  useEffect(() => {
    if (!isOpen || !isNew || createdId) return;
    const hasContent =
      !isFormPristine() ||
      !!form.description?.trim() ||
      !!form.categoryId ||
      pendingFiles.some((p) => p.key);
    if (!hasContent) return;
    writeInvoiceDraft({
      form,
      pendingFiles: pendingFiles.filter((p) => p.key).map(({ id, name, key }) => ({ id, name, key })),
      savedAt: Date.now(),
    });
  }, [form, pendingFiles, isOpen, isNew, createdId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ask the server to read the staged invoice file. Quiet by design: a miss just
  // means the user fills the form as usual.
  const runAiScan = async (key) => {
    if (!key || aiScanning || scannedKeysRef.current.has(key)) return;
    scannedKeysRef.current.add(key);
    setAiScanning(true);
    try {
      const suggestion = await extractInvoiceFromFile(key);
      if (suggestion) {
        setAiSuggestion(suggestion);
      } else {
        toast.info("Couldn't read the invoice automatically — please fill the details in.");
      }
    } finally {
      setAiScanning(false);
    }
  };

  // Maps a validated AI suggestion into the form, constraining every choice to
  // options that actually exist (unknown property/building fall back to blank so
  // a wrong guess is never silently committed). Vendor is left untouched when the
  // vendor is locked (self-submission).
  const applySuggestion = (s) => {
    setForm((prev) => {
      const groups = (s.groups && s.groups.length ? s.groups : [{}]).flatMap((g) => {
        const propertyId = propertyOptions.some((o) => o.value === g.propertyId) ? g.propertyId : "";
        const buildings = propertyId ? buildingOptionsFor(propertyId) : [];
        let buildingName = "";
        if (propertyId) {
          if (g.buildingName && buildings.some((b) => b.value === g.buildingName)) buildingName = g.buildingName;
          else if (buildings.length === 1) buildingName = buildings[0].value;
        }
        // A row is flat only when the reader said so; anything else stays hourly.
        const rows = (g.rows && g.rows.length ? g.rows : [{}]).map((r) => ({
          ...blankRow(),
          rateType: r.rateType === "flat" ? "flat" : "hourly",
          unitNo: r.unitNo ?? "",
          jobDescription: r.jobDescription ?? "",
          hours: r.hours ?? "",
          minutes: r.minutes ?? "",
          amountPerHour: r.amountPerHour ?? "",
          flatAmount: r.flatAmount ?? "",
          materialCost: r.materialCost ?? "",
          gstRate: r.gstRate ?? DEFAULT_GST_RATE,
          comment: r.comment ?? "",
        }));
        return [{ id: uid(), propertyId, buildingName, rows }];
      });
      // The extractor still reports the vendor's number per detected section, but
      // one paper invoice carries one number — take the first it found.
      const detectedRef = (s.groups || [])
        .map((g) => String(g?.referenceNo || "").trim())
        .find(Boolean) || "";
      return {
        ...prev,
        title: s.title || prev.title,
        description: s.description || prev.description,
        vendorId: vendorLocked ? prev.vendorId : (s.vendorId || prev.vendorId || ""),
        categoryId: s.categoryId || prev.categoryId || "",
        referenceNo: detectedRef || prev.referenceNo || "",
        groups: groups.length ? groups : prev.groups,
      };
    });
    setAiSuggestion(null);
    toast.success("Details prefilled — review and adjust before submitting.");
  };

  // A staged invoice file that's finished uploading can be (re)scanned on demand.
  const stagedScanKey = pendingFiles.find((p) => p.key && !p.uploading)?.key || null;
  const aiScanReady = isNew && !!stagedScanKey;
  const triggerManualScan = () => {
    if (!stagedScanKey) return;
    scannedKeysRef.current.delete(stagedScanKey);
    runAiScan(stagedScanKey);
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setErrors((p) => (p.attachments ? { ...p, attachments: undefined } : p));

    const room = MAX_INVOICE_FILES - pendingFiles.length - attachments.length;
    const picked = Array.from(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (room <= 0) {
      toast.warning(`Up to ${MAX_INVOICE_FILES} invoice files — remove one to add another.`);
      return;
    }
    if (picked.length > room) {
      toast.warning(`Up to ${MAX_INVOICE_FILES} invoice files — skipped ${picked.slice(room).map((f) => f.name).join(", ")}.`);
    }
    // Only the very first invoice file is offered to the AI, so a later page or
    // receipt can never re-prefill what was already filled in.
    const firstFile = pendingFiles.length === 0 && attachments.length === 0;
    for (const [i, file] of picked.slice(0, room).entries()) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOneInvoiceFile(file, firstFile && i === 0);
    }
  };

  const uploadOneInvoiceFile = async (file, scan) => {
    if (!invoiceId) {
      // New invoice: upload the file to a temporary staging area right away so it
      // gets a signed URL and previews exactly like a saved attachment. The key
      // is adopted by the invoice on create.
      const pf = {
        id: uid(),
        file,
        name: file.name,
        key: null,
        signedUrl: null,
        uploading: true,
        error: false,
      };
      setPendingFiles((prev) => [...prev, pf]);
      if (fileInputRef.current) fileInputRef.current.value = "";

      try {
        const key = await uploadStagingFile(pf.file);
        const urls = await getStagingSignedUrls([key]);
        setPendingFiles((prev) =>
          prev.map((p) =>
            p.id === pf.id ? { ...p, key, signedUrl: urls[key] || null, uploading: false } : p
          )
        );
        // Upload-first flow: read the invoice with AI and offer to prefill.
        if (scan && isFormPristine()) runAiScan(key);
      } catch (err) {
        console.error("staging upload failed", err);
        setPendingFiles((prev) =>
          prev.map((p) => (p.id === pf.id ? { ...p, uploading: false, error: true } : p))
        );
        toast.error(`Failed to upload ${pf.name}`);
      }
      return;
    }

    setUploading(true);
    try {
      const key = await uploadFileToInvoice(invoiceId, file);
      setAttachments((prev) => [...prev, key]);
      toast.success("File uploaded");
    } catch (err) {
      console.error("upload failed", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.key) removeStagingObject(removed.key).catch(() => {});
      return prev.filter((p) => p.id !== id);
    });
  };

  const openPendingPreview = (pf) => {
    if (pf?.signedUrl) setPreviewStaged({ signedUrl: pf.signedUrl, name: pf.name });
  };

  const handleRemove = async (key) => {
    try {
      await removeInvoiceAttachment(invoiceId, key);
      setAttachments((prev) => prev.filter((k) => k !== key));
      toast.success("Attachment removed");
    } catch {
      // handled
    }
  };

  // Header context shows the first section's property (with a "+N more" hint when
  // the submission spans several properties); the full editor lives in the body.
  const linkedGroups = (form.groups || []).filter((g) => g.propertyId);
  const firstPropLabel = linkedGroups.length
    ? propertyOptions.find((o) => o.value === linkedGroups[0].propertyId)?.label || ""
    : "";
  const headerPropertyLabel = firstPropLabel
    ? `${firstPropLabel}${linkedGroups.length > 1 ? ` +${linkedGroups.length - 1} more` : ""}`
    : (isNew ? prefilledPropertyLabel || "" : "");
  const headerBuildingName = linkedGroups.length > 1 ? "" : sectionBuildings(linkedGroups[0]).join(", ");

  // The PDF the server would generate from the vendor's template for the form
  // as it stands, shown before submitting.
  const openTemplatePreview = async () => {
    if (templatePreviewing) return;
    setTemplatePreviewing(true);
    try {
      const payload = { ...buildCreatePayload(), companyId: formCompanyId };
      if (effectiveVendorId) payload.vendorId = effectiveVendorId;
      setTemplatePreviewUrl(await previewEffectivePdf(payload));
    } catch {
      // toast shown by the api helper
    } finally {
      setTemplatePreviewing(false);
    }
  };
  const closeTemplatePreview = () => {
    if (templatePreviewUrl) URL.revokeObjectURL(templatePreviewUrl);
    setTemplatePreviewUrl(null);
  };

  // The invoice PDF the server generated from a template, if any. It is shared by
  // every line, so it shows in the Invoice File card; it cannot be removed by hand.
  const generatedInfo = invoiceData?.generatedFrom ||
    (previewGroup?.items || []).find((i) => i?.generatedFrom)?.generatedFrom || null;
  const billingGroupSnapshot = invoiceData?.billingGroup ||
    (previewGroup?.items || []).find((i) => i?.billingGroup)?.billingGroup || null;

  const handleClose = (refresh) => {
    if (isNew && !createdId) {
      cleanupStaged(pendingFiles);
      (form.groups || []).forEach((g) => (g.rows || []).forEach((r) => cleanupStaged(r.files)));
      clearInvoiceDraft();
    }
    setPendingFiles([]);
    onClose(refresh);
  };

  // Escape doesn't close the form: it is the key that closes a dropdown, and
  // closing here throws away everything typed (and the saved draft). The X and
  // Cancel still close it.
  return (
    <>
      <Modal isOpen={isOpen} toggle={() => handleClose(false)} size="xl" centered backdrop="static" keyboard={false}
        className="iv-form-modal-dialog" contentClassName="iv-form-modal-content">
        <ModalHeader toggle={() => handleClose(false)} style={{
          borderBottom: "1px solid #EEF2F7", padding: "16px 24px 14px",
          background: "#fff", borderRadius: "14px 14px 0 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, width: "100%" }}>
            <div style={{
              width: 46, height: 46, borderRadius: 12, flexShrink: 0,
              background: BLUE,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
            }}>
              <FeatherIcon icon={locked ? "eye" : isNew ? "file-plus" : "edit-2"} size={20} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>
                {locked
                  ? `Invoice ${formatInvoiceNo(invoiceData)}`
                  : isNew
                    ? "New Invoice"
                    : `Edit Invoice ${invoiceData?.invoiceNo ? `RD ${invoiceData.invoiceNo}` : ""}`}
              </div>
              {(headerPropertyLabel || headerBuildingName) ? (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, marginTop: 5,
                  fontSize: "0.78rem", color: "#475569", fontWeight: 600,
                }}>
                  <FeatherIcon icon="map-pin" size={12} color={BLUE} style={{ flexShrink: 0 }} />
                  <span style={{ color: "#0F172A" }}>{headerPropertyLabel || "—"}</span>
                  {headerBuildingName && (
                    <>
                      <span style={{ color: "#CBD5E1" }}>·</span>
                      <FeatherIcon icon="home" size={12} color="#64748B" style={{ flexShrink: 0 }} />
                      <span>{headerBuildingName}</span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: "0.78rem", color: "#64748B", fontWeight: 500, marginTop: 3 }}>
                  {locked
                    ? "Read-only view"
                    : isNew
                      ? (templateApplies
                        ? "Fill in the details below — your invoice is generated from your template"
                        : "Fill in the details below and attach the invoice file")
                      : "Update invoice details"}
                </div>
              )}
            </div>
            {billingGroupSnapshot?.name && (locked || !vendorLocked) && (
              <span className="iv-chip iv-chip--indigo" title="Property group" style={{ flexShrink: 0 }}>
                <FeatherIcon icon="layers" size={11} /> {billingGroupSnapshot.name}
              </span>
            )}
            {isNew && <PayablesCalendarPanel />}
            {locked && (
              <div className="iv-preview-actions">
                <button
                  type="button"
                  disabled={downloadingPdf}
                  onClick={async () => {
                    setDownloadingPdf(true);
                    try { await downloadInvoicePdf(invoiceData); } finally { setDownloadingPdf(false); }
                  }}
                  style={{
                    height: 38, fontWeight: 700, fontSize: "0.8rem", borderRadius: 9,
                    padding: "0 16px", background: "#fff", border: "1px solid #BFDBFE", color: BLUE_DARK,
                    display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
                    cursor: downloadingPdf ? "wait" : "pointer",
                    opacity: downloadingPdf ? 0.7 : 1,
                  }}
                >
                  {downloadingPdf ? <Spinner size="sm" style={{ width: 13, height: 13 }} /> : <FeatherIcon icon="download" size={14} />}
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={() => printInvoice(invoiceData)}
                  style={{
                    height: 38, fontWeight: 700, fontSize: "0.8rem", borderRadius: 9,
                    padding: "0 18px", background: BLUE, border: "none", color: "#fff",
                    display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
                    boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                    cursor: "pointer",
                  }}
                >
                  <FeatherIcon icon="printer" size={14} />
                  Print Invoice
                </button>
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "20px 26px", background: "#fff" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Without this a manager sees a smaller total than a lead does for
              the same invoice, with nothing on screen explaining the gap. */}
          {locked && previewGroup?.isPartial && (
            <div style={{
              background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 9,
              padding: "10px 13px", fontSize: "0.78rem", color: "#92400E",
            }}>
              <b>Showing {previewGroup.visibleItemCount} of {previewGroup.itemCount} items.</b>{" "}
              This invoice also covers properties outside your assignment. The totals
              below are your portion, not the invoice total.
            </div>
          )}

          {/* Announced politely as it appears: the file stops being required. */}
          <div aria-live="polite">
            {templateApplies && (
              <div role="note" style={{
                display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 12px",
                background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10,
                padding: "12px 14px", color: "#065F46", fontSize: "0.84rem", lineHeight: 1.45,
              }}>
                <FeatherIcon icon="check-circle" size={18} style={{ flexShrink: 0 }} />
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <b>Your invoice will be generated from “{effective.template.name}” (v{effective.template.version}).</b>{" "}
                  You don&apos;t need to attach an invoice file — add photos or receipts only if you want to.
                </div>
                <button
                  type="button"
                  onClick={openTemplatePreview}
                  disabled={templatePreviewing}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, minHeight: 40, padding: "8px 14px",
                    border: "1px solid #6EE7B7", borderRadius: 8, background: "#fff", color: "#065F46",
                    fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap", cursor: templatePreviewing ? "wait" : "pointer",
                  }}
                >
                  {templatePreviewing ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : <FeatherIcon icon="eye" size={14} />}
                  Preview invoice
                </button>
              </div>
            )}
          </div>

          {showFileRequiredNotice && (
            <div className={`iv-file-required${errors.attachments ? " is-error" : ""}`} role="note">
              <FeatherIcon icon="alert-circle" size={18} className="iv-file-required__icon" />
              <div className="iv-file-required__text">
                <b>An invoice file is required.</b> Attach a PDF, photo or screenshot of your
                invoice in the Invoice File section before submitting.
              </div>
              <button
                type="button"
                className="iv-file-required__btn"
                onClick={() => scrollToSection("file")}
              >
                <FeatherIcon icon="paperclip" size={14} /> Go to Invoice File
              </button>
            </div>
          )}

          {/* ── Section 1: Invoice Details ───────────────────────────── */}
          <div ref={sectionRefs.details} style={{ ...CARD_STYLE, scrollMarginTop: 12 }}>
            <SectionHeading icon="file-text" title="Invoice Details" />
            <div className="row g-3">
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Invoice Title <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => set("title")(e.target.value)}
                placeholder="e.g. February cleaning service"
                style={INPUT_STYLE}
                disabled={locked}
              />
              {errors.title && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Vendor <span style={{ color: "#DC2626" }}>*</span></Label>
              {vendorLocked ? (
                <div style={{
                  ...INPUT_STYLE, display: "flex", alignItems: "center", gap: 8,
                  padding: "0 12px", background: "#F8FAFC", color: "#0F172A", fontWeight: 600,
                  border: "1px solid #E2E8F0",
                }}>
                  <FeatherIcon icon="user" size={14} color={BLUE} />
                  {vendorName || "Your vendor account"}
                </div>
              ) : (
                <WithIcon icon="user">
                  <Select
                    options={vendorOpts}
                    value={selectedVendor}
                    onChange={(opt) => set("vendorId")(opt?.value || "")}
                    styles={selectStyles}
                    placeholder={vendorOpts.length ? "Choose vendor" : "No vendors yet — add one first"}
                    isDisabled={locked || vendorOpts.length === 0}
                    menuPortalTarget={document.body} menuPosition="fixed"
                  />
                </WithIcon>
              )}
              {errors.vendorId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.vendorId}</div>}
            </div>
            <div className="col-md-2">
              <Label style={LABEL_STYLE}>Category <span style={{ color: "#DC2626" }}>*</span></Label>
              <WithIcon icon="tag">
                <Select
                  options={categoryChoices}
                  value={categoryChoices.find((o) => o.value === form.categoryId) || null}
                  onChange={(opt) => set("categoryId")(opt?.value || "")}
                  styles={selectStyles}
                  placeholder={
                    categoryChoices.length
                      ? "Pick a category"
                      : (form.vendorId || vendorLocked ? "Vendor has no categories" : "Pick a vendor first")
                  }
                  isDisabled={locked || categoryChoices.length === 0}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </WithIcon>
              {errors.categoryId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.categoryId}</div>}
            </div>
            {/* Our number: one for the whole submission, assigned by the server. */}
            <div className="col-md-2">
              <Label style={LABEL_STYLE}>Invoice No.</Label>
              <div style={{
                ...INPUT_STYLE, display: "flex", alignItems: "center", gap: 8,
                padding: "0 12px", background: "#F8FAFC", color: "#1E40AF",
                fontWeight: 800, border: "1px solid #E2E8F0",
              }}>
                <FeatherIcon icon="hash" size={14} color={BLUE} />
                {isNew ? rdPreview : formatInvoiceNo(invoiceData)}
              </div>
            </div>
            {/* The vendor's own number, entered once for the whole invoice. */}
            <div className="col-md-2">
              <Label style={LABEL_STYLE}>Vendor&apos;s Invoice #</Label>
              <WithIcon icon="file-text">
                <Input
                  value={form.referenceNo || ""}
                  onChange={(e) => set("referenceNo")(e.target.value)}
                  placeholder="Optional"
                  style={INPUT_ICON_STYLE}
                  disabled={locked}
                />
              </WithIcon>
            </div>
            </div>{/* end row */}
          </div>{/* end Invoice Details card */}

          {/* ── Section 2: Properties & Costing ─────────────────────── */}
          <div ref={sectionRefs.lines} style={{ ...CARD_STYLE, scrollMarginTop: 12 }}>
            <SectionHeading icon="home" title="Properties & Costing" />

            {showGroupPicker && (
              <div style={{
                display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, marginBottom: 12,
                background: "#F8FAFC", border: "1px solid #E7EAF0", borderRadius: 12, padding: "12px 14px",
              }}>
                <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                  <label htmlFor="iv-billing-group" style={FIELD_LABEL}>Property group</label>
                  <WithIcon icon="layers">
                    <Select
                      inputId="iv-billing-group"
                      options={billingGroupOptions}
                      value={billingGroupOptions.find((o) => o.value === String(form.billingGroupId || "")) || billingGroupOptions[0]}
                      onChange={(opt) => {
                        const id = opt?.value || "";
                        const next = billingGroups.find((g) => String(g._id) === id) || null;
                        setForm((p) => ({ ...p, billingGroupId: id, groups: sectionsForGroup(p.groups, next, blankGroup) }));
                      }}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </WithIcon>
                  <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 4 }}>
                    {selectedBillingGroup
                      ? "Only this group's buildings are offered below. Each line still belongs to one building."
                      : "Billing several buildings together? Pick the group and only its buildings are offered."}
                  </div>
                </div>
                {selectedBillingGroup && (
                  <button
                    type="button"
                    onClick={addGroupBuildings}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "7px 14px",
                      border: "1px solid #BFDBFE", borderRadius: 9, background: "#fff", color: BLUE_DARK,
                      fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    <FeatherIcon icon="plus-square" size={14} />
                    Add all {(selectedBillingGroup.buildings || []).length} buildings
                  </button>
                )}
                {selectedBillingGroup && (
                  <div style={{ flex: "1 1 100%", display: "flex", flexDirection: "column", gap: 6 }}>
                    {groupBuildingsByProperty(selectedBillingGroup, properties).map((p) => (
                      <div key={p.propertyId} style={{ fontSize: "0.78rem", color: "#334155" }}>
                        <span style={{ fontWeight: 700 }}>{p.title}</span>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
                          {p.buildings.map((b) => <span key={b} className="iv-chip iv-chip--indigo">{b}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {totalRowCount > 1 && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10,
                padding: "8px 12px", fontSize: "0.76rem", color: BLUE_DARK,
              }}>
                <FeatherIcon icon="info" size={14} style={{ flexShrink: 0 }} />
                <span>
                  {isNew
                    ? "Every unit row becomes a numbered item on this one invoice — they share its Invoice No. and are reviewed together."
                    : "Every unit row below is its own line on this invoice. Saving updates them all — added rows are created and removed rows are deleted."}
                </span>
              </div>
            )}

            <input
              ref={rowFileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleRowFiles(e.target.files)}
            />

            {groupLoading && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "28px 0", color: "#64748B", fontSize: "0.82rem", fontWeight: 600,
              }}>
                <Spinner size="sm" color="primary" /> Loading every line of this invoice…
              </div>
            )}
            {!groupLoading && (groupLoadFailed || (!locked && groupPartial)) && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "28px 0", color: "#64748B", fontSize: "0.82rem", fontWeight: 600,
              }}>
                {groupLoadFailed
                  ? "Could not load every line of this invoice. Close and reopen it to edit."
                  : `You can see ${previewGroup.visibleItemCount} of ${previewGroup.itemCount} lines of this invoice, so it cannot be edited here.`}
              </div>
            )}
            <div style={{ display: linesUnusable ? "none" : "flex", flexDirection: "column", gap: 12 }}>
              {(form.groups || []).map((group, gi) => {
                const buildingOpts = buildingOptionsFor(group.propertyId);
                const groupTotal = (group.rows || []).reduce(
                  (a, r) => a + (computeLineMoney(rowSubtotal(r), r.gstRate).total || 0),
                  0
                );
                return (
                  <div key={group.id} style={{
                    background: "#F8FAFC", border: "1px solid #E7EAF0", borderRadius: 12,
                    padding: "16px 16px 14px",
                  }}>
                    {/* Section header: icon · title · unit count · section total · remove */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                          width: 36, height: 36, borderRadius: "50%", background: BLUE,
                          color: "#fff", fontSize: "0.9rem", fontWeight: 800,
                          display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
                        }}>
                          {gi + 1}
                        </span>
                        <span style={{ fontSize: "0.88rem", fontWeight: 700, color: "#0F172A" }}>
                          Property &amp; Building
                        </span>
                        <span style={{
                          background: "#F1F5F9", color: "#64748B", borderRadius: 20,
                          padding: "3px 10px", fontSize: "0.7rem", fontWeight: 700,
                        }}>
                          {(group.rows || []).length} unit{(group.rows || []).length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          background: "#DBEAFE", color: BLUE_DARK,
                          borderRadius: 8, padding: "5px 12px", fontSize: "0.78rem", fontWeight: 800,
                        }}>
                          {formatCurrency(groupTotal)}
                        </span>
                        {canEditLines && form.groups.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeGroup(gi)}
                            title="Remove property section"
                            style={{
                              ...ROW_BTN, width: 30, height: 30, borderRadius: 8,
                              border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626",
                            }}
                          >
                            <FeatherIcon icon="trash-2" size={13} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Property · Building */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.6fr",
                      gap: 12, marginBottom: 12,
                    }}>
                      <div>
                        <div style={FIELD_LABEL}>Property {!locked && <span style={{ color: "#DC2626" }}>*</span>}</div>
                        {/* Read-only View shows the name the SERVER resolved for
                            this line, not a lookup in whatever property list this
                            viewer happens to hold. An auditor's list is empty and
                            a manager's is scoped, so a disabled Select would find
                            no option and render blank on a real invoice. */}
                        {locked ? (
                          <div style={STATIC_FIELD}>
                            <FeatherIcon icon="map-pin" size={14} color={BLUE} />
                            <span style={STATIC_FIELD_TEXT}>
                              {group.propertyLabel
                                || propertyOptions.find((o) => o.value === group.propertyId)?.label
                                || "—"}
                            </span>
                          </div>
                        ) : (
                          <WithIcon icon="map-pin">
                            <Select
                              options={propertyOptions}
                              value={propertyOptions.find((o) => o.value === group.propertyId) || null}
                              onChange={(opt) => onPickProperty(gi, opt?.value || "")}
                              styles={selectStyles}
                              placeholder="Select property"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </WithIcon>
                        )}
                      </div>
                      <div>
                        <div style={FIELD_LABEL}>Building {!locked && <span style={{ color: "#DC2626" }}>*</span>}</div>
                        {locked ? (
                          <div style={STATIC_FIELD}>
                            <FeatherIcon icon="grid" size={14} color={BLUE} />
                            <span style={STATIC_FIELD_TEXT}>{sectionBuildings(group).join(", ") || "—"}</span>
                          </div>
                        ) : (
                          <WithIcon icon="grid">
                            {/* Where lines can be added, several of the property's
                                buildings may share this section (each row then
                                picks its own); an older single-line invoice keeps
                                one building. */}
                            <Select
                              isMulti={canEditLines}
                              closeMenuOnSelect={!canEditLines}
                              options={canEditLines ? [...buildingOpts, MULTI_BUILDING_OPTION] : buildingOpts}
                              value={canEditLines
                                ? buildingOpts.filter((o) => sectionBuildings(group).includes(o.value))
                                : buildingOpts.find((o) => o.value === group.buildingName) || null}
                              onChange={(opt) => {
                                if (!canEditLines) { updateGroup(gi, { buildingName: opt?.value || "" }); return; }
                                const picked = opt || [];
                                if (picked.some((o) => o.value === MULTI_BUILDING_OPTION.value)) { setBuildingPickerFor(gi); return; }
                                setGroupBuildings(gi, picked.map((o) => o.value));
                              }}
                              styles={selectStyles}
                              placeholder={group.propertyId ? "Pick building" : canEditLines ? "Pick property first, or multiple" : "Pick property first"}
                              isDisabled={!group.propertyId && !canEditLines}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </WithIcon>
                        )}
                        {!locked && selectedBillingGroup && !sectionInGroup(group, selectedBillingGroup) && (
                          <div role="alert" style={{ display: "flex", gap: 5, alignItems: "flex-start", color: "#B45309", fontSize: "0.72rem", fontWeight: 600, marginTop: 4 }}>
                            <FeatherIcon icon="alert-triangle" size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                            <span>{buildingsOutsideGroup(group, selectedBillingGroup).join(", ")} {buildingsOutsideGroup(group, selectedBillingGroup).length > 1 ? "are" : "is"} not in “{selectedBillingGroup.name}”. Pick one of its buildings or change the group.</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Unit rows for this property */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {(group.rows || []).map((row, ri) => {
                        const rateType = row.rateType || "hourly";
                        const isFlat = rateType === "flat";
                        const subtotal = rowSubtotal(row, rateType);
                        const m = computeLineMoney(subtotal, row.gstRate);
                        // Hourly: Unit No. · Description · Rate Type · Hours · Minutes · Amount/Hr · Material · GST
                        // Flat:   Unit No. · Description · Rate Type · Amount · Material · GST
                        // The row's money sits in the note row below, styled like the invoice totals bar.
                        const rowColumns = isFlat
                          ? `0.8fr 1.6fr 196px 1.2fr 1.2fr 0.8fr${canEditLines ? (isNew ? " 138px" : " 92px") : ""}`
                          : `0.7fr 1.4fr 196px 0.65fr 0.65fr 0.9fr 0.9fr 0.65fr${canEditLines ? (isNew ? " 138px" : " 92px") : ""}`;
                        return (
                          <div key={row.id} style={{
                            background: "#fff", border: "1px solid #E7EAF0", borderRadius: 10,
                            padding: "12px 14px",
                          }}>
                            {sectionBuildings(group).length > 1 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                                <div style={{ ...FIELD_LABEL, marginBottom: 0 }}>
                                  Building {!locked && <span style={{ color: "#DC2626" }}>*</span>}
                                </div>
                                <div style={{ flex: "0 1 300px", minWidth: 200 }}>
                                  <Select
                                    aria-label="Building for this row"
                                    options={sectionBuildings(group).map((b) => ({ value: b, label: b }))}
                                    value={row.buildingName ? { value: row.buildingName, label: row.buildingName } : null}
                                    onChange={(opt) => updateRow(gi, ri, { buildingName: opt?.value || "" })}
                                    styles={selectStyles}
                                    placeholder="Which building?"
                                    isDisabled={locked}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />
                                </div>
                              </div>
                            )}
                            {/* Keyed by rate type so a switch remounts the field
                                set and plays the short fade, instead of inputs
                                morphing in place under the user's cursor. */}
                            <div key={rateType} className="iv-rate-fields" style={{
                              display: "grid",
                              gridTemplateColumns: rowColumns,
                              gap: 10,
                            }}>
                              {/* One of these two must be filled — see validate(). */}
                              <div>
                                <div style={FIELD_LABEL}>Unit No.</div>
                                <WithIcon icon="home">
                                  <Input
                                    value={row.unitNo}
                                    onChange={(e) => updateRow(gi, ri, { unitNo: e.target.value })}
                                    placeholder="e.g. 101"
                                    aria-label="Unit number"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Description</div>
                                <DescriptionCombo
                                  value={row.jobDescription}
                                  onChange={(v) => updateRow(gi, ri, { jobDescription: v })}
                                  options={descriptionPresets}
                                  disabled={locked}
                                />
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Rate Type</div>
                                <RateTypeToggle
                                  name={`rate-type-${row.id}`}
                                  value={rateType}
                                  onChange={(next) => updateRow(gi, ri, { rateType: next })}
                                  disabled={locked}
                                />
                              </div>
                              {isFlat ? (
                              <div>
                                <div style={FIELD_LABEL}>Amount ($)</div>
                                <WithIcon icon="dollar-sign">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={row.flatAmount}
                                    onChange={(e) => updateRow(gi, ri, { flatAmount: e.target.value })}
                                    placeholder="0.00"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              ) : (
                              <>
                              <div>
                                <div style={FIELD_LABEL}>Hours</div>
                                <WithIcon icon="clock">
                                  <Input
                                    type="number" step="1" min="0"
                                    value={row.hours}
                                    onChange={(e) => updateRow(gi, ri, { hours: e.target.value.replace(/[^0-9]/g, "") })}
                                    placeholder="0"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Minutes</div>
                                <WithIcon icon="clock">
                                  <Input
                                    type="number" step="1" min="0" max="59"
                                    value={row.minutes}
                                    onChange={(e) => {
                                      const digits = e.target.value.replace(/[^0-9]/g, "");
                                      const clamped = digits === "" ? "" : String(Math.min(59, Number(digits)));
                                      updateRow(gi, ri, { minutes: clamped });
                                    }}
                                    placeholder="0"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Amount / Hr ($)</div>
                                <WithIcon icon="dollar-sign">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={row.amountPerHour}
                                    onChange={(e) => updateRow(gi, ri, { amountPerHour: e.target.value })}
                                    placeholder="0.00"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              </>
                              )}
                              <div>
                                <div style={FIELD_LABEL}>Material Cost ($)</div>
                                <WithIcon icon="package">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={row.materialCost}
                                    onChange={(e) => updateRow(gi, ri, { materialCost: e.target.value })}
                                    placeholder="0.00"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>GST %</div>
                                <WithIcon icon="percent">
                                  <Input
                                    type="number" step="0.1" min="0" max="100"
                                    value={row.gstRate}
                                    onChange={(e) => updateRow(gi, ri, { gstRate: e.target.value })}
                                    placeholder="5"
                                    style={INPUT_ICON_STYLE}
                                    disabled={locked}
                                  />
                                </WithIcon>
                              </div>
                              {canEditLines && (
                                <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
                                  {/* Per-row files are uploaded at creation only. */}
                                  {isNew && (
                                    <button
                                      type="button"
                                      onClick={() => openRowFilePicker(group.id, row.id)}
                                      title="Attach files to this row"
                                      style={{
                                        ...ROW_BTN,
                                        border: "1px solid #BFDBFE", background: "#fff", color: BLUE,
                                      }}
                                    >
                                      <FeatherIcon icon="paperclip" size={15} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => addRow(gi)}
                                    title="Add another unit row"
                                    style={{
                                      ...ROW_BTN,
                                      border: "1px solid #BFDBFE", background: "#fff", color: BLUE,
                                    }}
                                  >
                                    <FeatherIcon icon="plus" size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeRow(gi, ri)}
                                    disabled={(group.rows || []).length <= 1}
                                    title="Remove this row"
                                    style={{
                                      ...ROW_BTN,
                                      border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626",
                                      cursor: (group.rows || []).length <= 1 ? "not-allowed" : "pointer",
                                      opacity: (group.rows || []).length <= 1 ? 0.4 : 1,
                                    }}
                                  >
                                    <FeatherIcon icon="trash-2" size={15} />
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Note + row attachments, then this row's totals on the right */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                              <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                                <Input
                                  value={row.comment}
                                  onChange={(e) => updateRow(gi, ri, { comment: e.target.value })}
                                  placeholder={locked ? "No note for this row" : "Note for this row (optional)"}
                                  style={{ ...INPUT_ICON_STYLE, height: 44, fontSize: "0.8rem" }}
                                  disabled={locked}
                                />
                                <FeatherIcon
                                  icon="align-left" size={14} color="#94A3B8"
                                  style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                                />
                              </div>
                              {(row.files || []).map((f) => (
                                <span key={f.id} style={{
                                  display: "inline-flex", alignItems: "center", gap: 6,
                                  background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 20,
                                  padding: "4px 10px", fontSize: "0.72rem", fontWeight: 600, color: BLUE_DARK,
                                  maxWidth: 220,
                                }}>
                                  {f.uploading
                                    ? <Spinner size="sm" style={{ width: 12, height: 12, flexShrink: 0 }} />
                                    : <FeatherIcon icon="paperclip" size={11} style={{ flexShrink: 0 }} />}
                                  <span
                                    onClick={() => f.signedUrl && setPreviewStaged({ signedUrl: f.signedUrl, name: f.name })}
                                    title={f.name}
                                    style={{
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                      cursor: f.signedUrl ? "zoom-in" : "default",
                                    }}
                                  >
                                    {f.name}
                                  </span>
                                  {/* Saved row files are read-only here; only new staged ones can be dropped. */}
                                  {!locked && !f.saved && (
                                    <FeatherIcon
                                      icon="x" size={11}
                                      style={{ cursor: "pointer", flexShrink: 0 }}
                                      onClick={() => removeRowFile(group.id, row.id, f.id)}
                                    />
                                  )}
                                </span>
                              ))}
                              <RowTotals subtotal={m.subtotal} gst={m.gstAmount} total={m.total} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.lines && (
              <div style={{ color: "#DC2626", fontSize: "0.74rem", marginTop: 8 }}>{errors.lines}</div>
            )}

            {/* Totals summary — hidden while the lines are still loading, since
                a total over the single-line placeholder would be wrong. */}
            <div style={{
              display: linesUnusable ? "none" : "flex", justifyContent: "flex-end", alignItems: "stretch",
              marginTop: 16, background: "#1E3A8A", borderRadius: 10, overflow: "hidden",
              borderLeft: "5px solid #F59E0B",
            }}>
              <div style={{ padding: "13px 22px", textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>Subtotal</div>
                <div style={{ fontSize: "1rem", color: "#fff", fontWeight: 800, marginTop: 2 }}>{formatCurrency(subtotalSum)}</div>
              </div>
              <div style={{ padding: "13px 22px", textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>GST</div>
                <div style={{ fontSize: "1rem", color: "#fff", fontWeight: 800, marginTop: 2 }}>{formatCurrency(gstSum)}</div>
              </div>
              <div style={{ padding: "13px 26px", background: "rgba(255,255,255,0.14)", textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem", color: "#FCD34D", fontWeight: 700 }}>Grand Total</div>
                <div style={{ fontSize: "1.2rem", color: "#FBBF24", fontWeight: 900, marginTop: 2 }}>{formatCurrency(totalSum)}</div>
              </div>
            </div>
          </div>{/* end Properties card */}

          {/* ── Section 3: Additional Notes ─────────────────────────── */}
          <div style={CARD_STYLE}>
            <SectionHeading icon="edit-3" title="Additional Notes" />
            <div style={{ display: "flex", border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
              <div style={{
                width: 44, background: "#FEF3C7", display: "flex",
                alignItems: "flex-start", justifyContent: "center", paddingTop: 14, flexShrink: 0,
              }}>
                <FeatherIcon icon="file-text" size={16} color="#D97706" />
              </div>
              <Input
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                disabled={locked}
                placeholder={locked ? "No additional notes" : "Add any extra context, scope, or instructions for this invoice…"}
                style={{
                  border: "none", borderRadius: 0, boxShadow: "none",
                  fontSize: "0.85rem", minHeight: 90, padding: "12px 14px", resize: "vertical",
                }}
              />
            </div>
          </div>

          {/* ── Section 4: Invoice File ─────────────────────────────── */}
          <div
            ref={sectionRefs.file}
            style={{
              ...CARD_STYLE,
              scrollMarginTop: 12,
              ...(errors.attachments ? { border: "2px solid #DC2626", boxShadow: "0 0 0 3px rgba(220,38,38,0.12)" } : {}),
            }}
          >
              <SectionHeading
                icon="paperclip"
                title={isNew && !templateApplies ? <>Invoice File <span style={{ color: "#DC2626" }}>*</span></> : "Invoice File"}
                subtitle={locked
                  ? "The files submitted with this invoice"
                  : templateApplies
                    ? "Optional — your invoice PDF is generated from your template. Add photos or receipts if you like"
                    : "Required — attach the invoice files (or a file on a line). Upload first and AI can prefill the form"}
                right={<>
                {isNew && aiScanning && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#EEF2FF", color: BLUE_DARK, border: "1px solid #C7D2FE",
                    borderRadius: 20, padding: "6px 14px", fontSize: "0.76rem", fontWeight: 700,
                  }}>
                    <Spinner size="sm" style={{ width: 14, height: 14 }} /> Reading invoice…
                  </span>
                )}
                {isNew && !aiScanning && aiScanReady && (
                  <button
                    type="button"
                    onClick={triggerManualScan}
                    title="Read this invoice with AI and prefill the form"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "linear-gradient(135deg,#6366F1,#2563EB)", border: "none", color: "#fff",
                      borderRadius: 20, padding: "7px 14px", fontSize: "0.76rem", fontWeight: 700,
                      cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.28)",
                    }}
                  >
                    <FeatherIcon icon="zap" size={13} /> Autofill with AI
                  </button>
                )}
                </>}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {errors.attachments && (
                <div role="alert" style={{
                  display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10,
                  color: "#B91C1C", fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.4,
                }}>
                  <FeatherIcon icon="alert-circle" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{errors.attachments} — a PDF, photo or screenshot of your invoice is fine.</span>
                </div>
              )}
              {(pendingFiles.length === 0 && attachments.length === 0) ? (
                locked ? (
                  <div style={{
                    border: "1px solid #E7EAF0", borderRadius: 12, padding: "22px 20px",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    color: "#94A3B8", fontSize: "0.84rem", fontWeight: 600,
                  }}>
                    <FeatherIcon icon="paperclip" size={16} color="#CBD5E1" />
                    No file was attached to this invoice
                  </div>
                ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                  style={{
                    border: `2px dashed ${dragOver ? BLUE : errors.attachments ? "#FCA5A5" : "#CBD5E1"}`, borderRadius: 12,
                    background: dragOver ? "#EFF6FF" : "transparent",
                    padding: "26px 20px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 12, flexWrap: "wrap", transition: "all 0.12s",
                  }}
                >
                  <span style={{
                    width: 44, height: 44, borderRadius: 12, background: "#EFF6FF",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <FeatherIcon icon="paperclip" size={18} color={BLUE} />
                  </span>
                  <span style={{ color: "#334155", fontSize: "0.86rem", fontWeight: 600 }}>
                    Drag &amp; drop files here or
                  </span>
                  <button
                    type="button"
                    disabled={uploading || saving}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      background: "#DBEAFE", border: "none", color: BLUE_DARK,
                      borderRadius: 8, padding: "8px 16px", fontSize: "0.8rem", fontWeight: 700,
                      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {uploading ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : null}
                    Choose Files
                  </button>
                  <span style={{ color: "#94A3B8", fontSize: "0.78rem" }}>PDF, image, Excel, Word or CSV · up to {MAX_INVOICE_FILES}</span>
                </div>
                )
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                  {/* Pending (uploaded to staging, not yet on the invoice) */}
                  {pendingFiles.map((pf) => {
                    const filename = pf.name;
                    const t = getFileTypeFromKey(filename);
                    const canPreview = !!pf.signedUrl && !pf.uploading;
                    return (
                      <div key={pf.id} className="iv-attach-card">
                        <div
                          className="iv-attach-card__preview"
                          onClick={() => canPreview && openPendingPreview(pf)}
                          style={{ cursor: canPreview ? "zoom-in" : "default" }}
                        >
                          {pf.uploading
                            ? <Spinner size="sm" style={{ color: BLUE }} />
                            : t === "image" && pf.signedUrl
                              ? <img src={pf.signedUrl} alt={filename} />
                              : t === "pdf"
                                ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                : t === "office"
                                  ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                  : <FeatherIcon icon="file" size={32} color="#64748B" />}
                          {pf.uploading && <span className="iv-attach-card__badge">UPLOADING</span>}
                        </div>
                        <div className="iv-attach-card__name" title={filename}>{filename}</div>
                        <div className="iv-attach-card__actions">
                          {canPreview && (
                            <button
                              type="button"
                              onClick={() => openPendingPreview(pf)}
                              title="Preview"
                            >
                              <FeatherIcon icon="eye" size={12} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemovePending(pf.id)}
                            title="Remove"
                            style={{ color: "#DC2626" }}
                            disabled={saving || pf.uploading}
                          >
                            <FeatherIcon icon="trash-2" size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Server-confirmed attachments */}
                  {attachments.map((key) => {
                    const filename = key.split("/").pop();
                    const url = signedUrls[key];
                    const t = getFileTypeFromKey(key);
                    return (
                      <div key={key} className="iv-attach-card">
                        <div className="iv-attach-card__preview" onClick={() => url && setPreviewKey(key)}>
                          {t === "image" && url
                            ? <img src={url} alt={filename} />
                            : t === "pdf"
                              ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                              : t === "office"
                                ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                : <FeatherIcon icon="file" size={32} color="#64748B" />}
                        </div>
                        <div className="iv-attach-card__name" title={filename}>{filename}</div>
                        {generatedInfo?.key === key && (
                          <div style={{ padding: "0 8px 6px" }}>
                            <span className="iv-chip iv-chip--green" title="Created by the server from the vendor's approved template">
                              <FeatherIcon icon="zap" size={10} />
                              Generated from {generatedInfo.templateName || "template"} v{generatedInfo.version}
                            </span>
                          </div>
                        )}
                        <div className="iv-attach-card__actions">
                          <button
                            type="button"
                            onClick={() => url && setPreviewKey(key)}
                            title="Preview"
                          >
                            <FeatherIcon icon="eye" size={12} />
                          </button>
                          {url && (
                            <a href={url} target="_blank" rel="noopener noreferrer" title="Download">
                              <FeatherIcon icon="download" size={12} />
                            </a>
                          )}
                          {!locked && generatedInfo?.key !== key && (
                            <button
                              type="button"
                              onClick={() => handleRemove(key)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Room for more: click or drop to add another invoice file */}
                  {!locked && pendingFiles.length + attachments.length < MAX_INVOICE_FILES && (
                    <button
                      type="button"
                      disabled={uploading || saving}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                      title="Add another invoice file"
                      style={{
                        border: `2px dashed ${dragOver ? BLUE : "#CBD5E1"}`, borderRadius: 12,
                        background: dragOver ? "#EFF6FF" : "transparent", minHeight: 120,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        gap: 6, color: BLUE_DARK, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      {uploading ? <Spinner size="sm" style={{ color: BLUE }} /> : <FeatherIcon icon="plus" size={20} color={BLUE} />}
                      Add file
                    </button>
                  )}
                </div>
              )}
          </div>{/* end Invoice File card */}

          </div>{/* end outer flex column */}
        </ModalBody>
        <ModalFooter style={{
          borderTop: "1px solid #E9EEF5", padding: "14px 24px",
          background: "#F8FAFC", borderRadius: "0 0 14px 14px",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button
            type="button"
            onClick={() => handleClose(false)}
            disabled={saving || uploading}
            style={{
              height: 44, fontWeight: 600, fontSize: "0.85rem", borderRadius: 10,
              padding: "0 22px", background: "#fff", border: "1px solid #E2E8F0", color: "#334155",
              cursor: saving || uploading ? "not-allowed" : "pointer",
            }}
          >
            {locked ? "Close" : "Cancel"}
          </button>
          {locked ? null : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || uploading || groupPending || pendingFiles.some((p) => p.uploading) || anyRowFileUploading}
              style={{
                height: 44, fontWeight: 700, fontSize: "0.85rem", borderRadius: 10,
                padding: "0 24px", background: BLUE, border: "none", color: "#fff",
                display: "inline-flex", alignItems: "center", gap: 8,
                boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                cursor: "pointer",
                opacity: (saving || uploading || groupPending || pendingFiles.some((p) => p.uploading) || anyRowFileUploading) ? 0.6 : 1,
              }}
            >
              {saving ? <Spinner size="sm" /> : <FeatherIcon icon={isNew ? "send" : "save"} size={15} />}
              {isNew ? "Create Invoice" : "Save Changes"}
            </button>
          )}
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        isOpen={!!previewKey}
        toggle={() => setPreviewKey(null)}
        signedUrl={previewKey ? signedUrls[previewKey] : null}
        fileKey={previewKey}
      />

      <FilePreviewModal
        isOpen={!!previewStaged}
        toggle={() => setPreviewStaged(null)}
        signedUrl={previewStaged?.signedUrl || null}
        fileKey={previewStaged?.name || null}
      />

      <Modal isOpen={!!templatePreviewUrl} toggle={closeTemplatePreview} size="xl" centered>
        <ModalHeader toggle={closeTemplatePreview}>Invoice preview</ModalHeader>
        <ModalBody style={{ padding: 0, height: "78vh" }}>
          {templatePreviewUrl && (
            <iframe title="Invoice preview" src={templatePreviewUrl} style={{ width: "100%", height: "100%", border: 0 }} />
          )}
        </ModalBody>
        <ModalFooter>
          <span style={{ fontSize: "0.76rem", color: "#64748B", marginRight: "auto" }}>
            The final PDF is created when you submit; the invoice number may move on if another invoice is submitted first.
          </span>
          <Button color="secondary" outline onClick={closeTemplatePreview}>Close</Button>
        </ModalFooter>
      </Modal>

      <PrefillReviewModal
        isOpen={!!aiSuggestion}
        suggestion={aiSuggestion}
        onConfirm={() => applySuggestion(aiSuggestion)}
        onDismiss={() => setAiSuggestion(null)}
      />

      <BuildingPickerModal
        isOpen={buildingPickerFor != null}
        choices={buildingChoices}
        existing={(form.groups || []).flatMap((g) => sectionBuildings(g).map((b) => ({ propertyId: g.propertyId, buildingName: b })))}
        onConfirm={addPickedBuildings}
        onCancel={() => setBuildingPickerFor(null)}
      />
    </>
  );
};

export { InvoiceFormModal };
